import type { CycleCollectionView } from "@/services/types";
import { PrintLayout } from "@/components/feature/PrintLayout";
import { formatINRNumber } from "@/utils/format";

interface CollectionSheetPrintProps {
  view: CycleCollectionView;
}

export function CollectionSheetPrint({ view }: CollectionSheetPrintProps) {
  const { demands } = view;
  const expected = demands.reduce((s, d) => s + d.netPayable, 0);
  const collected = demands.reduce((s, d) => s + d.paid, 0);
  const balance = expected - collected;

  return (
    <PrintLayout
      title="Collection Sheet"
      meta={
        <>
          {view.groupName} ({view.groupCode}) · Cycle {view.cycle} — {view.periodLabel}
        </>
      }
    >
      <table className="w-full border-collapse text-sm">
        <thead className="print:table-header-group">
          <tr className="border-b-2 border-foreground-900 text-left print:break-inside-avoid">
            <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Ticket</th>
            <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Member</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Commission</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Net payable</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Paid</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Balance</th>
          </tr>
        </thead>
        <tbody>
          {demands.map((d) => (
            <tr key={d.id} className="border-b border-foreground-200/60 print:break-inside-avoid">
              <td className="tnum px-3 py-2 font-medium">{d.ticketNumber}</td>
              <td className="px-3 py-2 font-medium text-foreground-900">
                {d.memberName}
                <span className="tnum ml-1 text-xs text-foreground-500 font-normal">({d.memberPhone})</span>
              </td>
              <td className="tnum px-3 py-2 text-right">{d.commission > 0 ? formatINRNumber(d.commission) : "—"}</td>
              <td className="tnum px-3 py-2 text-right font-semibold">{formatINRNumber(d.netPayable)}</td>
              <td className="tnum px-3 py-2 text-right font-semibold">{formatINRNumber(d.paid)}</td>
              <td className="tnum px-3 py-2 text-right font-medium">{formatINRNumber(d.balance)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="print:table-footer-group">
          <tr className="border-t-2 border-foreground-900 font-semibold print:break-inside-avoid">
            <td className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider">Total</td>
            <td className="px-3 py-2.5" />
            <td className="tnum px-3 py-2.5 text-right font-bold text-base">{formatINRNumber(demands.reduce((s, d) => s + (d.commission || 0), 0))}</td>
            <td className="tnum px-3 py-2.5 text-right font-bold text-base">{formatINRNumber(expected)}</td>
            <td className="tnum px-3 py-2.5 text-right font-bold text-base">{formatINRNumber(collected)}</td>
            <td className="tnum px-3 py-2.5 text-right font-bold text-base">{formatINRNumber(balance)}</td>
          </tr>
        </tfoot>
      </table>
      <p className="mt-3 text-xs text-foreground-500 print:break-inside-avoid">
        Collected by staff for the {view.groupName} chit fund. This sheet is verified against the cash in hand at day end.
      </p>
      <div className="mt-10 flex justify-between border-t border-foreground-200 pt-2 text-xs text-foreground-500 print:break-inside-avoid">
        <span>Recorded by: ______________</span>
        <span>Verified by: ______________</span>
      </div>
    </PrintLayout>
  );
}